import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, MessageSquare, Sparkles, Send, ExternalLink } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { usePersonPanel } from "@/context/PersonPanelContext";
import { RelationBadge, CommitmentBadge } from "@/components/Badges";
import { ChannelIcon } from "@/components/BrandIcons";
import { gravatarUrl, initials, formatDate, relTime } from "@/lib/utils-fos";

// Global slide-in panel — opens whenever `usePersonPanel().openPerson(id)` is called.
export function PersonPanel() {
  const nav = useNavigate();
  const { openId, closePerson, byId } = usePersonPanel();
  const person = openId ? byId[openId] : null;
  const [commitments, setCommitments] = useState([]);
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [draft, setDraft] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);

  useEffect(() => {
    if (!openId) { setDraft(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const [cc, ev, tt, pr] = await Promise.all([
          endpoints.commitments(), endpoints.events(), endpoints.tasks(), endpoints.projects(),
        ]);
        if (cancelled) return;
        setCommitments(cc.filter((c) => c.from_person_id === openId || c.to_person_id === openId));
        setEvents(ev.filter((e) => (e.people_ids || []).includes(openId)));
        setTasks(tt.filter((t) => (t.mentions || []).includes(openId) || t.owner === (byId[openId]?.name)));
        setProjects(pr);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [openId, byId]);

  const linkedProjects = useMemo(() => {
    const ids = new Set();
    commitments.forEach((c) => c.project_id && ids.add(c.project_id));
    events.forEach((e) => (e.project_ids || []).forEach((pid) => ids.add(pid)));
    tasks.forEach((t) => t.project_id && ids.add(t.project_id));
    return [...ids].map((id) => projects.find((p) => p.id === id)).filter(Boolean);
  }, [commitments, events, tasks, projects]);

  const doDraft = async () => {
    if (!person) return;
    setDraftLoading(true);
    try {
      const r = await endpoints.draftMessage(person.id, "follow_up");
      setDraft(r.message || "");
    } catch (e) { toast.error("Draft failed"); }
    finally { setDraftLoading(false); }
  };

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape" && openId) closePerson(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [openId, closePerson]);

  if (!openId || !person) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40 animate-fade-in" onClick={closePerson}>
      <div
        className="w-full max-w-lg h-full overflow-y-auto bg-white border-l border-neutral-200 shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="person-panel"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-neutral-900">{person.name}</h2>
            <button
              onClick={() => { closePerson(); nav(`/people?focus=${person.id}`); }}
              className="p-1 rounded hover:bg-neutral-100 text-neutral-500"
              data-testid="person-panel-open-full"
              title="Open in People page"
            >
              <ExternalLink size={14} />
            </button>
          </div>
          <button onClick={closePerson} data-testid="person-panel-close" className="p-1 rounded hover:bg-neutral-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex items-start gap-3 mb-5">
          <Avatar url={person.avatar_url} name={person.name} email={person.email} />
          <div className="flex-1 min-w-0">
            <p className="text-sm">{person.role || "—"}</p>
            <p className="text-xs text-neutral-500">{person.company || "—"}</p>
            <div className="mt-2"><RelationBadge type={person.relationship_type} /></div>
          </div>
        </div>

        <div className="fos-card p-3 mb-4">
          <p className="text-[10px] mono mb-2 text-neutral-500">CONTACT</p>
          <p className="text-sm">{person.email || "—"}</p>
          {person.whatsapp && <p className="text-sm">WA: {person.whatsapp}</p>}
          {person.slack_id && <p className="text-sm">Slack: {person.slack_id}</p>}
        </div>

        {(person.communication_preference || person.sensitivities) && (
          <div className="fos-card p-3 mb-4">
            <p className="text-[10px] mono mb-2 text-neutral-500">PREFERENCES</p>
            {person.communication_preference && (
              <p className="text-sm mb-1"><span className="mono text-[10px] text-neutral-500">PREFERS · </span>{person.communication_preference}</p>
            )}
            {person.sensitivities && (
              <p className="text-sm text-[#F97316]">· {person.sensitivities}</p>
            )}
          </div>
        )}

        <button
          data-testid="person-panel-reach-out"
          onClick={doDraft}
          disabled={draftLoading}
          className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 mb-4"
        >
          <MessageSquare size={14} />
          {draftLoading ? "Drafting…" : "Reach out (AI draft)"}
        </button>
        {draft && (
          <div className="fos-card p-3 mb-4" data-testid="person-panel-draft-output">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={12} className="text-neutral-500" />
              <span className="text-[10px] mono text-neutral-500">AI DRAFT</span>
            </div>
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{draft}</pre>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copied"); }}
                className="text-xs px-3 py-1.5 rounded-sm border border-neutral-200"
              >
                Copy
              </button>
              <button
                onClick={() => toast.success(`Message sent to ${person.name}`)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-neutral-900 text-white"
              >
                <Send size={11} /> Send
              </button>
            </div>
          </div>
        )}

        {commitments.length > 0 && (
          <div className="fos-card p-3 mb-4">
            <p className="text-[10px] mono mb-2 text-neutral-500">COMMITMENTS ({commitments.length})</p>
            <div className="space-y-2">
              {commitments.map((c) => {
                const proj = projects.find((p) => p.id === c.project_id);
                return (
                  <div key={c.id} className="text-sm flex items-start gap-2">
                    <CommitmentBadge status={c.status} />
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{c.what}</p>
                      <p className="text-[10px] text-neutral-500 mt-0.5">
                        {proj && <span>{proj.name} · </span>}
                        Due {formatDate(c.due_date)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tasks.length > 0 && (
          <div className="fos-card p-3 mb-4">
            <p className="text-[10px] mono mb-2 text-neutral-500">TASKS ({tasks.length})</p>
            <div className="space-y-1.5">
              {tasks.slice(0, 8).map((t) => {
                const proj = projects.find((p) => p.id === t.project_id);
                return (
                  <button
                    key={t.id}
                    onClick={() => { closePerson(); nav(`/projects?focus=${t.project_id}&highlight=task-${t.id}`); }}
                    className="w-full text-left text-sm flex items-start gap-2 p-1.5 rounded hover:bg-neutral-100"
                  >
                    <span className="text-neutral-500 mt-0.5">
                      {t.status === "done" ? "✓" : t.status === "in_progress" ? "◐" : "○"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`truncate ${t.status === "done" ? "line-through text-neutral-400" : ""}`}>{t.title}</p>
                      {proj && <p className="text-[10px] text-neutral-500">{proj.name}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {events.length > 0 && (
          <div className="fos-card p-3 mb-4">
            <p className="text-[10px] mono mb-2 text-neutral-500">RECENT ACTIVITY</p>
            <div className="space-y-2">
              {events.slice(0, 6).map((e) => (
                <div key={e.id} className="text-sm flex items-start gap-2">
                  <ChannelIcon channel={e.source_channel} size={12} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="leading-snug">{e.content_summary}</p>
                    <p className="text-[10px] text-neutral-500 mt-0.5">{relTime(e.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {linkedProjects.length > 0 && (
          <div className="fos-card p-3">
            <p className="text-[10px] mono mb-2 text-neutral-500">LINKED PROJECTS</p>
            <div className="flex flex-wrap gap-1.5">
              {linkedProjects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { closePerson(); nav(`/projects?focus=${p.id}`); }}
                  className="text-[11px] mono px-2 py-0.5 rounded bg-neutral-100 hover:bg-neutral-200 text-neutral-800 transition-colors"
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Avatar({ url, name, email }) {
  const [src, setSrc] = useState(url || gravatarUrl(email, 96) || null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setSrc(url || gravatarUrl(email, 96) || null);
    setFailed(false);
  }, [url, email]);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => {
          const g = gravatarUrl(email, 96);
          if (src !== g && g) setSrc(g);
          else setFailed(true);
        }}
        className="h-14 w-14 rounded-full object-cover shrink-0 border border-neutral-200"
      />
    );
  }
  return (
    <div className="h-14 w-14 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 bg-neutral-200 text-neutral-800">
      {initials(name)}
    </div>
  );
}
